import type { ClusterSegment, ClusterUnitRow } from "./clusterRing";

export interface ClusterTileRow extends Omit<ClusterUnitRow, "speed"> {
  color: string;
  /** Ya formateada ("45 km/h") o vacía si no se mueve. */
  speedText: string;
}

interface ClusterTileProps {
  count: number;
  segments: ClusterSegment[];
  /** Color de «conduciendo» si alguna unidad del grupo se mueve; sin él no hay punto. */
  movingColor?: string;
  rows?: ClusterTileRow[];
  more?: number;
  moreLabel?: (n: number) => string;
}

// Ficha del marcador de grupo. Antes era un anillo cónico alrededor de un
// disco blanco, casi idéntico al de Mapon (Daniel, 21-oct-2026: «más
// propio»). Ahora: número grande sobre fondo oscuro, legible en mapa claro y
// en satélite, y debajo la barra de estados en orden fijo (clusterRing.ts).
//
// Al pasar el mouse muestra qué unidades hay (pedido de Daniel, como Mapon).
// Es CSS puro (group-hover del contenedor en MapView): sin estado ni
// listeners por grupo, y con un retardo de 120 ms para que cruzar el mapa con
// el mouse no haga parpadear tarjetas.
export default function ClusterTile({ count, segments, movingColor, rows = [], more = 0, moreLabel }: ClusterTileProps) {
  return (
    <>
      <div className="h-full rounded-[13px] bg-gray-950/90 ring-2 ring-white shadow-[0_6px_16px_-4px_rgba(0,0,0,0.45)] flex flex-col items-center justify-center gap-[3px] px-2 backdrop-blur-sm">
        <span className="text-white font-semibold text-[15px] leading-none tabular-nums tracking-tight">{count}</span>
        <div className="flex gap-[2px] w-full max-w-[30px] h-[4px]">
          {segments.map((seg) => (
            <span
              key={seg.status}
              className="h-full rounded-full"
              style={{ flexGrow: seg.count, flexBasis: 0, minWidth: "3px", backgroundColor: seg.color }}
            />
          ))}
        </div>
      </div>
      {/* Alguien del grupo va conduciendo: el punto late para que se note sin
          tener que leer la barra. */}
      {movingColor && (
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping"
            style={{ backgroundColor: movingColor }}
          />
          <span className="relative inline-flex h-3 w-3 rounded-full ring-2 ring-white" style={{ backgroundColor: movingColor }} />
        </span>
      )}
      {rows.length > 0 && (
        <div
          role="tooltip"
          className="absolute left-full top-1/2 ml-3 w-[220px] -translate-y-1/2 translate-x-1 rounded-xl bg-white/95 p-1.5 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.35)] ring-1 ring-black/5 backdrop-blur-sm invisible opacity-0 pointer-events-none transition-[opacity,transform,visibility] duration-150 ease-out group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 group-hover:delay-[120ms] motion-reduce:transition-none"
        >
          <ul>
            {rows.map((r) => (
              <li key={r.id} className="flex h-8 items-center gap-2 rounded-lg px-2">
                <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white shadow" style={{ backgroundColor: r.color }} />
                <span className="flex-1 truncate text-[13px] font-medium text-gray-900">{r.name}</span>
                {r.speedText && (
                  <span className="shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] tabular-nums text-gray-600">{r.speedText}</span>
                )}
                {r.status === "driving" && (
                  <i
                    aria-hidden
                    className="ri-navigation-fill shrink-0 text-[13px] text-gray-500"
                    // ri-navigation-fill apunta al noroeste (-45°); medido en el
                    // navegador: con +45 un rumbo de 90° apunta al este.
                    style={{ transform: `rotate(${r.course + 45}deg)` }}
                  />
                )}
              </li>
            ))}
          </ul>
          {more > 0 && moreLabel && (
            <p className="border-t border-gray-100 px-2 pb-0.5 pt-1.5 text-[11px] text-gray-500">{moreLabel(more)}</p>
          )}
        </div>
      )}
    </>
  );
}
